/**
 * Processing Status Component
 * Real-time status visualization during analysis/processing.
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle2, Clock, Loader, AlertCircle } from 'lucide-react';

const STATUS_STEPS = [
  { key: 'uploaded', label: 'Uploaded', icon: CheckCircle2 },
  { key: 'analyzing', label: 'Analyzing', icon: Loader },
  { key: 'analyzed', label: 'Ready', icon: CheckCircle2 },
  { key: 'processing', label: 'Detecting PII', icon: Loader },
  { key: 'processed', label: 'Complete', icon: CheckCircle2 }
];

const PHASE_LABELS = {
  detecting: 'Scanning for personal information...',
  cross_page: 'Cross-referencing across pages...',
  regex: 'Running pattern safety checks...',
  annotating: 'Generating highlighted PDF...',
  uploading: 'Saving results...'
};

function formatElapsed(seconds) {
  if (seconds < 60) {
    return `${seconds}s elapsed`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')} elapsed`;
}

function getTimeEstimate(status, pageCount) {
  if (status === 'analyzing') {
    return 'Usually takes 5-15 seconds';
  }
  if (status === 'processing' && pageCount) {
    const estimate = pageCount * 15 + 10;
    return `~${estimate} seconds for ${pageCount} page${pageCount !== 1 ? 's' : ''}`;
  }
  return null;
}

export default function ProcessingStatus({ document }) {
  const [elapsed, setElapsed] = useState(0);

  const currentStatus = document?.status;
  const progress = document?.processingProgress;
  const pageCount = document?.pageCount || document?.analysis?.totalPages;

  // Elapsed timer
  useEffect(() => {
    if (!document) return;
    if (!['analyzing', 'processing'].includes(currentStatus)) return;

    const startField = currentStatus === 'analyzing'
      ? document.analyzingStartedAt
      : document.processingStartedAt;

    // Calculate initial elapsed from server timestamp
    let startTime;
    if (startField?.toDate) {
      startTime = startField.toDate().getTime();
    } else if (startField?.seconds) {
      startTime = startField.seconds * 1000;
    } else {
      startTime = Date.now();
    }

    const updateElapsed = () => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [document, currentStatus]);

  if (!document) {
    return null;
  }

  // Don't show if document is in error state or already processed
  if (currentStatus === 'error' || currentStatus === 'processed') {
    return null;
  }

  // Only show during active processing states
  if (!['analyzing', 'processing'].includes(currentStatus)) {
    return null;
  }

  const currentStepIndex = STATUS_STEPS.findIndex(step => step.key === currentStatus);
  const timeEstimate = getTimeEstimate(currentStatus, pageCount);
  const phaseLabel = progress?.phase ? PHASE_LABELS[progress.phase] : null;
  const progressPercent = progress?.totalPages
    ? Math.round((progress.currentPage / progress.totalPages) * 100)
    : null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      {/* Header with elapsed timer */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            {currentStatus === 'analyzing' ? 'Analyzing Document...' : 'Processing PII Detection...'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {elapsed > 0 && (
            <span className="text-xs font-mono text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
              {formatElapsed(elapsed)}
            </span>
          )}
        </div>
      </div>

      {/* Time estimate */}
      {timeEstimate && (
        <p className="text-xs text-blue-600 mb-3">{timeEstimate}</p>
      )}

      {/* Per-page progress bar (only during processing with progress data) */}
      {currentStatus === 'processing' && progress && progress.totalPages > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-blue-800 mb-1">
            <span>{phaseLabel || 'Processing...'}</span>
            {progress.phase === 'detecting' && (
              <span>Page {progress.currentPage} of {progress.totalPages}</span>
            )}
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STATUS_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          const isPending = index > currentStepIndex;

          return (
            <React.Fragment key={step.key}>
              {/* Step Icon and Label */}
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full border-2
                    ${isActive ? 'border-blue-600 bg-blue-100 text-blue-600' : ''}
                    ${isCompleted ? 'border-green-600 bg-green-100 text-green-600' : ''}
                    ${isPending ? 'border-gray-300 bg-gray-50 text-gray-400' : ''}
                  `}
                >
                  <Icon
                    className={`w-4 h-4 ${isActive ? 'animate-spin' : ''}`}
                  />
                </div>
                <span
                  className={`
                    text-xs font-medium
                    ${isActive ? 'text-blue-900' : ''}
                    ${isCompleted ? 'text-green-900' : ''}
                    ${isPending ? 'text-gray-400' : ''}
                  `}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector Line */}
              {index < STATUS_STEPS.length - 1 && (
                <div className="flex-1 h-0.5 mx-2">
                  <div
                    className={`
                      h-full
                      ${index < currentStepIndex ? 'bg-green-600' : 'bg-gray-300'}
                    `}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Status Message */}
      <div className="mt-3 text-xs text-blue-700">
        {currentStatus === 'analyzing' && (
          <p>Analyzing document type and estimating credits...</p>
        )}
        {currentStatus === 'processing' && !phaseLabel && (
          <p>Detecting and extracting personal information...</p>
        )}
      </div>
    </div>
  );
}
